/**
 * VideoRenderer Component
 * Displays embedded videos associated with a question
 */

import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import type { Question } from '../../../types';

interface VideoRendererProps {
  question: Question;
}

function VideoRenderer({ question }: VideoRendererProps): JSX.Element {
  const videos = question.videos || [];

  if (videos.length === 0) {
    return <Box />;
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>
          {videos.length > 1 ? 'Videos' : 'Video'}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {videos.map((video) => (
            <Box key={video.id}>
              <Typography variant="body2" gutterBottom>
                {video.title}
              </Typography>

              {/* Video Embed */}
              <Box
                sx={{
                  position: 'relative',
                  paddingBottom: '56.25%',
                  height: 0,
                  overflow: 'hidden',
                  borderRadius: 1,
                  bgcolor: '#000',
                }}
              >
                <iframe
                  src={video.embedUrl}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={video.title}
                />
              </Box>

              {video.duration && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  Duration: {Math.floor(video.duration / 60)}:{String(video.duration % 60).padStart(2, '0')}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

export default VideoRenderer;
